/* global AbortController */
import { Controller } from '@hotwired/stimulus'

// Generation stays on this page. The file is held until the download button
// is clicked, and leaving the page drops the request.
export default class extends Controller {
  static targets = ['from', 'to', 'generate', 'loading', 'error', 'download']
  static values = { url: String, today: String }

  connect () {
    this.fileUrl = null
    this.leftPage = false
    this.generation = 0
  }

  disconnect () {
    this.leftPage = true
    this.generation += 1
    this.abortGeneration()
  }

  datesChanged () {
    this.generation += 1
    this.abortGeneration()
    this.hideDownload()
    this.hideLoading()
    this.enableButtons()
  }

  async generate (event) {
    const generation = ++this.generation
    this.abortGeneration()
    this.hideDownload()
    this.hideError()

    const from = this.fromTarget.value
    const to = this.toTarget.value
    if (!from || !to) {
      this.showError('Enter a start date and an end date.')
      return
    }
    if (to < from) {
      this.showError('End date must be on or after the start date.')
      return
    }
    if (from > this.todayValue) {
      this.showError('Start date cannot be in the future.')
      return
    }

    const format = event.params.format
    this.disableButtons()
    this.showLoading()
    const abortController = new AbortController()
    this.abortController = abortController
    const timeout = setTimeout(() => abortController.abort(), 5 * 60 * 1000)

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        headers: {
          Accept: format === 'csv' ? 'text/csv' : 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: new URLSearchParams({ from, to, export_format: format }),
        signal: abortController.signal
      })
      if (this.leftPage || generation !== this.generation) return

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}))
        this.showError(payload.error || 'The report could not be generated.')
        return
      }

      const blob = await response.blob()
      if (this.leftPage || generation !== this.generation) return
      const filename = this.filename(response) || `WMATA_full_reports_${from}_${to}.${format}`
      this.rememberDownload(blob, filename)
    } catch (error) {
      if (this.leftPage || generation !== this.generation) return
      if (error.name === 'AbortError') {
        this.showError('The report took too long. Try a shorter date range.')
      } else {
        this.showError('The report could not be generated.')
      }
    } finally {
      clearTimeout(timeout)
      if (generation === this.generation) {
        this.hideLoading()
        this.enableButtons()
      }
    }
  }

  download () {
    if (!this.fileUrl) return

    const link = document.createElement('a')
    link.href = this.fileUrl
    link.download = this.downloadTarget.textContent
    link.click()
  }

  rememberDownload (blob, filename) {
    this.hideDownload()
    this.fileUrl = URL.createObjectURL(blob)
    this.downloadTarget.textContent = filename
    this.downloadTarget.classList.remove('hidden')
  }

  hideDownload () {
    if (this.fileUrl) URL.revokeObjectURL(this.fileUrl)
    this.fileUrl = null
    if (this.hasDownloadTarget) this.downloadTarget.classList.add('hidden')
  }

  abortGeneration () {
    if (this.abortController) this.abortController.abort()
    this.abortController = null
    clearTimeout(this.timeout)
  }

  filename (response) {
    const header = response.headers.get('Content-Disposition') || ''
    const match = header.match(/filename="([^"]+)"/)
    return match && match[1]
  }

  disableButtons () {
    this.generateTargets.forEach((button) => { button.disabled = true })
  }

  enableButtons () {
    this.generateTargets.forEach((button) => { button.disabled = false })
  }

  showLoading () {
    this.loadingTarget.classList.remove('hidden')
  }

  hideLoading () {
    if (this.hasLoadingTarget) this.loadingTarget.classList.add('hidden')
  }

  showError (message) {
    this.errorTarget.textContent = message
    this.errorTarget.classList.remove('hidden')
  }

  hideError () {
    this.errorTarget.textContent = ''
    this.errorTarget.classList.add('hidden')
  }
}
